import React, { useState, useEffect } from "react"
import classNames from "classnames"
import Popup from "../Popup/Popup"
import Button from "../Button/Button"
import styles from "./InvitedCompaniesModal.module.scss"
import opportunityServices from "../../services/opportunityServices"
import Pagination from "../elements/Pagination"
import DropdownListSort from "../DropdownListSort"
import ThreeDotsMenu from "../ThreeDotsMenu/ThreeDotsMenu"
import defaultUserAvatar from "../../images/defaultUserAvatar.png"
import companyDefaultIcon from "../../images/icons/companyDefaultIcon.svg"

const InvitedCompaniesModal = ({ opportunityId, opportunityName, isOpen, onClose }) => {
  const [invitedList, setInvitedList] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [sortBy, setSortBy] = useState("newest")
  const itemsPerPage = 10

  useEffect(() => {
    if (isOpen && opportunityId) {
      fetchInvitedCompanies()
    }
  }, [isOpen, opportunityId, currentPage])

  const fetchInvitedCompanies = async () => {
    setLoading(true)
    try {
      const response = await opportunityServices.getInvitedCompanies(
        opportunityId,
        currentPage,
      )

      if (response?.data?.invited_companies) {
        setInvitedList(response.data.invited_companies)
        setTotalPages(response.data.total_pages || 1)
        setTotalCount(response.data.total_count || 0)
      }
    } catch (error) {
      console.error("Error fetching invited companies:", error)
      setInvitedList([])
    } finally {
      setLoading(false)
    }
  }

  const modalTitle = opportunityName 
    ? `${opportunityName} / Invited Companies/Professionals`
    : "Invited Companies/Professionals"

  const sortOptions = [
    { value: "newest", label: "Newest" },
    { value: "oldest", label: "Oldest" },
    { value: "a_to_z", label: "A to Z" },
    { value: "z_to_a", label: "Z to A" },
  ]

  const handleSortChange = (option) => {
    setSortBy(option.value)
    // TODO: Implement sorting on backend or client-side
    // For now, backend returns sorted by newest first
  }

  const remainingInvites = 200 - totalCount

  return (
    <Popup
      width={800}
      title={modalTitle}
      isCrossIcon={true}
      closeAction={onClose}
      closeByOutsideClick={true}
      className={styles.modal}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <div className={styles.sortWrapper}>
            <DropdownListSort
              options={sortOptions}
              selectedValue={sortBy}
              onChange={handleSortChange}
            />
          </div>
          {totalCount > 0 && (
            <div className={styles.counter}>
              {remainingInvites}/200 invites remaining
            </div>
          )}
        </div>
        {loading ? (
          <div className={styles.loading}>Loading...</div>
        ) : invitedList.length === 0 ? (
          <div className={styles.emptyState}>
            No invited companies/professionals yet
          </div>
        ) : (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Company/User</th>
                    <th>Email</th>
                    <th>Invite Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {invitedList.map((item, index) => {
                    const companyName = item.company_name || item.companyName || 'N/A'
                    const email = item.email || 'N/A'
                    const photo = item.photo || null
                    const inviteStatus = item.invite_status || item.inviteStatus || 'Invited'
                    
                    // Use default icon if no photo (companyDefaultIcon for companies, defaultUserAvatar for users)
                    // For now, we'll use companyDefaultIcon as default since we can't distinguish company vs user easily
                    const logoUrl = photo || companyDefaultIcon
                    
                    return (
                      <tr key={`invited-${index}-${item.email || index}`}>
                        <td className={styles.companyNameCell}>
                          <div className={styles.companyNameWrapper}>
                            <img
                              src={logoUrl}
                              alt={companyName}
                              className={styles.companyLogo}
                              onError={(e) => {
                                e.target.src = companyDefaultIcon
                              }}
                            />
                            <span>{companyName}</span>
                          </div>
                        </td>
                        <td className={styles.emailCell}>
                          <span>{email}</span>
                        </td>
                        <td>
                          <span
                            className={classNames(styles.status, {
                              [styles.invited]:
                                inviteStatus === "Invited" ||
                                inviteStatus === "Invite sent",
                              [styles.accepted]: inviteStatus === "Accepted",
                              [styles.declined]: inviteStatus === "Declined",
                            })}
                          >
                            {inviteStatus}
                          </span>
                        </td>
                        <td className={styles.actionsCell}>
                          <ThreeDotsMenu actions={[]} />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <Pagination
                  currentPageNumber={currentPage}
                  totalPageCount={totalPages}
                  onSelect={(page) => setCurrentPage(page)}
                />
              </div>
            )}
          </>
        )}
        <div className={styles.modalFooter}>
          <Button buttonStyle="outlined" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Popup>
  )
}

export default InvitedCompaniesModal
